import { Fragment, useEffect, useRef, useState } from 'react';
import { InlineMath } from 'react-katex';

import 'katex/dist/katex.min.css';

import styles from './MathText.module.css';

interface MathTextProps {
  text: string;
}

type MathSegment =
  | { type: 'math'; value: string }
  | { type: 'text'; value: string };

// Kudu's math delimiter is $$...$$ on BOTH sides, and nothing else.
//
// A single $ is ordinary text. It used to open an equation, so "What is $5 plus
// $3?" rendered "5 plus " as KaTeX — currency, prices, and shell snippets were
// all silently turned into math. The proto has always documented the convention
// ("It may contain LaTeX equations as long as they are enclosed by $$'s") and
// every authoring surface — the equation editor, the equation modal — writes
// $$...$$, so restricting to it preserves authored content while fixing the
// accidental cases.
function tokenizeMath(text: string): MathSegment[] {
  const segments: MathSegment[] = [];
  let cursor = 0;

  while (cursor < text.length) {
    const start = text.indexOf('$$', cursor);

    if (start === -1) {
      segments.push({ type: 'text', value: text.slice(cursor) });
      break;
    }

    if (start > cursor) {
      segments.push({ type: 'text', value: text.slice(cursor, start) });
    }

    const end = text.indexOf('$$', start + 2);

    if (end === -1) {
      // Unterminated: the rest is literal, including the opening $$. Better a
      // visible $$ than swallowing the remainder of the paragraph into math.
      segments.push({ type: 'text', value: text.slice(start) });
      break;
    }

    const math = text.slice(start + 2, end);

    if (math.length > 0) {
      segments.push({ type: 'math', value: math });
    }

    cursor = end + 2;
  }

  return segments.filter((segment) => segment.value.length > 0);
}

function decodeHtmlEntities(text: string) {
  if (!text.includes('&') || typeof document === 'undefined') {
    return text;
  }

  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

/**
 * A standalone equation, in its own scroll container.
 *
 * KaTeX lays math out as `.base` spans that never wrap internally, so an
 * expression with no top-level operator to break at is a single unbreakable
 * box. Given no constraint it makes its container as wide as itself, which
 * pushed the whole page past the viewport.
 *
 * The container is only focusable when it HAS somewhere to scroll. A scroll
 * region that a keyboard cannot reach fails WCAG 2.1.1, but making every
 * equation a tab stop would bury a keyboard user on an equation-heavy chapter —
 * so the tab stop appears exactly when it buys something.
 */
function ScrollableMath({ math }: { math: string }) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return undefined;
    }

    const measure = () => {
      setIsScrollable(element.scrollWidth > element.clientWidth + 1);
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    // Re-measure on layout changes: the sidebar opening, a rotation, or a
    // window resize all change whether the same equation still fits.
    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, [math]);

  return (
    <span
      className={styles.scrollableMath}
      ref={ref}
      tabIndex={isScrollable ? 0 : undefined}
    >
      <InlineMath math={math} />
    </span>
  );
}

export function MathText({ text }: MathTextProps) {
  const segments = tokenizeMath(text);

  // An equation that is the WHOLE string stands on its own — that is how
  // display-style math is authored here — so it gets the scroll container.
  // Math mixed into a sentence is left exactly as it was: wrapping it would
  // change the baseline and the line-breaking of ordinary prose for no gain,
  // since short inline math is not what overflows.
  if (segments.length === 1 && segments[0].type === 'math') {
    return <ScrollableMath math={segments[0].value} />;
  }

  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === 'math') {
          return <InlineMath key={`math-${index}`} math={segment.value} />;
        }

        return (
          <Fragment key={`text-${index}`}>
            {decodeHtmlEntities(segment.value)}
          </Fragment>
        );
      })}
    </>
  );
}
